/**
 * Enhanced Monitoring Dashboard
 * Real-time monitoring and analytics with integrated Phase 4 components
 * 
 * @description Professional monitoring interface featuring:
 * - Real-time metrics visualization with interactive charts
 * - Multi-cloud resource performance monitoring
 * - Alert management and notification system
 * - Custom dashboards with configurable widgets
 * - Performance analytics and trend analysis
 * - SLA monitoring and reporting
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useCloudProviderStore } from "@/stores/cloudProviderStore";
import { 
  getLocalProviderFilterOptions,
  getFilterSummary 
} from "@/utils/providerFiltering";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Activity,
  Cpu,
  HardDrive,
  Wifi,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Server,
  Database,
  Network,
  Shield,
  Clock,
  Settings,
  RefreshCw,
  Download,
  Bell,
  Eye
} from "lucide-react";

// Enhanced monitoring data types
interface MetricData {
  timestamp: string;
  cpu: number;
  memory: number;
  disk: number;
  network: number;
}

interface Alert {
  id: string;
  resource: string;
  type: 'critical' | 'warning' | 'info';
  message: string;
  timestamp: string;
  status: 'active' | 'resolved';
}

interface ResourceMetrics {
  id: string;
  name: string;
  type: 'compute' | 'storage' | 'database' | 'network';
  provider: 'aws' | 'gcp' | 'azure';
  status: 'running' | 'stopped' | 'error';
  health: 'healthy' | 'warning' | 'critical';
  cpu: number;
  memory: number;
  disk: number;
  network: number;
  uptime: number;
  lastUpdate: string;
}

// Mock real-time data generation
const generateMetricData = (): MetricData[] => {
  const now = Date.now();
  return Array.from({ length: 24 }, (_, i) => ({
    timestamp: new Date(now - (23 - i) * 3600000).toISOString(),
    cpu: Math.random() * 80 + 10,
    memory: Math.random() * 70 + 20,
    disk: Math.random() * 60 + 15,
    network: Math.random() * 100 + 50
  }));
};

const mockAlerts: Alert[] = [
  {
    id: 'alert-001',
    resource: 'web-server-prod-01',
    type: 'warning',
    message: 'High CPU usage detected (85%)',
    timestamp: '2024-01-20T10:30:00Z',
    status: 'active'
  },
  {
    id: 'alert-002',
    resource: 'user-database-main',
    type: 'critical',
    message: 'Disk usage critical (95%)',
    timestamp: '2024-01-20T09:15:00Z',
    status: 'active'
  },
  {
    id: 'alert-003',
    resource: 'analytics-worker',
    type: 'info',
    message: 'Backup completed successfully',
    timestamp: '2024-01-20T08:00:00Z',
    status: 'resolved'
  }
];

const mockResourceMetrics: ResourceMetrics[] = [
  {
    id: 'aws-ec2-001',
    name: 'web-server-prod-01',
    type: 'compute',
    provider: 'aws',
    status: 'running',
    health: 'warning',
    cpu: 85.2,
    memory: 67.8,
    disk: 45.3,
    network: 125.4,
    uptime: 99.5,
    lastUpdate: '2024-01-20T10:35:00Z'
  },
  {
    id: 'aws-rds-001',
    name: 'user-database-main',
    type: 'database',
    provider: 'aws',
    status: 'running',
    health: 'critical',
    cpu: 42.1,
    memory: 78.9,
    disk: 95.2,
    network: 89.7,
    uptime: 99.9,
    lastUpdate: '2024-01-20T10:35:00Z'
  },
  {
    id: 'gcp-vm-001',
    name: 'analytics-worker',
    type: 'compute',
    provider: 'gcp',
    status: 'running',
    health: 'healthy',
    cpu: 35.7,
    memory: 52.3,
    disk: 38.9,
    network: 67.2,
    uptime: 98.8,
    lastUpdate: '2024-01-20T10:35:00Z'
  }
];

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#8b5cf6'
};

const typeIcons = {
  compute: Server,
  storage: HardDrive,
  database: Database,
  network: Network
};

const healthColors = {
  healthy: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
};

const alertTypeColors = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
};

export default function MonitoringPage() {
  // Global provider state
  const globalProvider = useCloudProviderStore(state => state.provider);
  
  const [metricData, setMetricData] = useState<MetricData[]>(generateMetricData());
  const [resourceMetrics, setResourceMetrics] = useState<ResourceMetrics[]>(mockResourceMetrics);
  const [alerts] = useState<Alert[]>(mockAlerts);
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>("24h");
  const [isRealTime, setIsRealTime] = useState(true);

  // Filter data based on global provider and local filters
  const filteredResourceMetrics = resourceMetrics.filter(resource => {
    // First filter by global provider
    if (resource.provider !== globalProvider) return false;
    
    // Then apply local provider filter within global scope
    if (providerFilter === "all") return true;
    return resource.provider === providerFilter;
  });

  // Real-time data updates
  useEffect(() => {
    if (!isRealTime) return;
    
    const interval = setInterval(() => {
      setMetricData(generateMetricData());
      
      // Update resource metrics with slight variations
      setResourceMetrics(prev => prev.map(resource => ({
        ...resource,
        cpu: Math.max(0, Math.min(100, resource.cpu + (Math.random() - 0.5) * 10)),
        memory: Math.max(0, Math.min(100, resource.memory + (Math.random() - 0.5) * 8)),
        disk: Math.max(0, Math.min(100, resource.disk + (Math.random() - 0.5) * 3)),
        network: Math.max(0, resource.network + (Math.random() - 0.5) * 20),
        lastUpdate: new Date().toISOString()
      })));
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [isRealTime]);

  const getAverageMetric = (metricType: keyof MetricData) => {
    if (metricType === 'timestamp') return 0;
    return metricData.reduce((sum, data) => sum + (data[metricType] as number), 0) / metricData.length;
  };

  const getActiveAlerts = () => alerts.filter(alert => alert.status === 'active');

  const getResourcesByHealth = () => {
    const counts = { healthy: 0, warning: 0, critical: 0 };
    resourceMetrics.forEach(resource => {
      counts[resource.health]++;
    });
    return [
      { name: 'Healthy', value: counts.healthy, color: COLORS.success },
      { name: 'Warning', value: counts.warning, color: COLORS.warning },
      { name: 'Critical', value: counts.critical, color: COLORS.danger }
    ];
  };

  const filteredResources = filteredResourceMetrics;

  const healthData = getResourcesByHealth();
  const activeAlerts = getActiveAlerts();

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Monitoring</h1>
          <p className="text-muted-foreground">
            Real-time infrastructure monitoring and analytics
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={isRealTime ? "default" : "outline"}
            size="sm"
            onClick={() => setIsRealTime(!isRealTime)}
          >
            <Activity className="w-4 h-4 mr-2" />
            {isRealTime ? "Live" : "Paused"}
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Configure
          </Button>
        </div>
      </div>

      {/* Overview Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg CPU Usage</p>
                <p className="text-2xl font-bold text-foreground">
                  {getAverageMetric('cpu').toFixed(1)}%
                </p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <TrendingDown className="w-3 h-3 mr-1" />
                  -2.5% from yesterday
                </p>
              </div>
              <Cpu className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Memory Usage</p>
                <p className="text-2xl font-bold text-foreground">
                  {getAverageMetric('memory').toFixed(1)}%
                </p>
                <p className="text-xs text-red-600 flex items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  +1.2% from yesterday
                </p>
              </div>
              <HardDrive className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Network I/O</p>
                <p className="text-2xl font-bold text-foreground">
                  {getAverageMetric('network').toFixed(1)} MB/s
                </p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  +5.8% from yesterday
                </p>
              </div>
              <Wifi className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Alerts</p>
                <p className="text-2xl font-bold text-foreground">{activeAlerts.length}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {alerts.filter(a => a.type === 'critical').length} critical
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resource Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Resource Performance</span>
              <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">Last Hour</SelectItem>
                  <SelectItem value="24h">Last 24h</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={metricData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis 
                  dataKey="timestamp" 
                  stroke="#6b7280"
                  tickFormatter={(value) => new Date(value).getHours() + ':00'}
                />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleTimeString()}
                />
                <Line 
                  type="monotone" 
                  dataKey="cpu" 
                  stroke={COLORS.primary} 
                  strokeWidth={2}
                  name="CPU %"
                />
                <Line 
                  type="monotone" 
                  dataKey="memory" 
                  stroke={COLORS.success} 
                  strokeWidth={2}
                  name="Memory %"
                />
                <Line 
                  type="monotone" 
                  dataKey="disk" 
                  stroke={COLORS.warning} 
                  strokeWidth={2}
                  name="Disk %"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Resource Health Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Resource Health Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={healthData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }: any) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {healthData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Resource Metrics Table and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resource Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Resource Metrics</span>
              <div className="flex items-center gap-2">
                <div className="text-sm text-muted-foreground">
                  {getFilterSummary(globalProvider, providerFilter)}
                </div>
                <Select value={providerFilter} onValueChange={setProviderFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getLocalProviderFilterOptions(globalProvider).map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredResources.map((resource) => {
                const TypeIcon = typeIcons[resource.type];
                return (
                  <div key={resource.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <TypeIcon className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">{resource.name}</p>
                          <p className="text-sm text-muted-foreground">{resource.provider.toUpperCase()}</p>
                        </div>
                      </div>
                      <Badge className={healthColors[resource.health]}>
                        {resource.health}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="flex justify-between">
                          <span>CPU</span>
                          <span className="font-medium">{resource.cpu.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${resource.cpu}%` }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between">
                          <span>Memory</span>
                          <span className="font-medium">{resource.memory.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${resource.memory}%` }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between">
                          <span>Disk</span>
                          <span className="font-medium">{resource.disk.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-yellow-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${resource.disk}%` }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between">
                          <span>Uptime</span>
                          <span className="font-medium">{resource.uptime.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${resource.uptime}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Active Alerts</span>
              <Button variant="outline" size="sm">
                <Bell className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.slice(0, 10).map((alert) => (
                <div key={alert.id} className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={alertTypeColors[alert.type]}>
                          {alert.type}
                        </Badge>
                        <span className="text-sm font-medium text-foreground">
                          {alert.resource}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {alert.message}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                      {alert.status === 'active' && (
                        <Button variant="ghost" size="sm">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {alerts.length === 0 && (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">All Clear!</h3>
                  <p className="text-muted-foreground">
                    No active alerts. Your infrastructure is running smoothly.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}